// eslint-disable-next-line import/no-unresolved
import { AddOns } from "@/utils/interface";
import {
  INSURANCE_QUOTE_TO,
  type CommercialCreditCheck,
  type EmploymentIncomeVerificationMonths,
  type EmploymentIncomeVerificationReportPdfStatus,
  type EmploymentIncomeVerificationStatus,
// eslint-disable-next-line import/no-unresolved
} from "@/constants";
// eslint-disable-next-line import/no-unresolved
import { type LodgeAI } from "@/providers/LenderCardContextProvider/types/LenderProductTypes";
import { LenderErrorV1, LenderError } from "./lenders";

enum RepaymentFrequency {
  Weekly = "Weekly",
  Fortnightly = "Fortnightly",
  Monthly = "Monthly",
}

interface ApplicationFormSlice {
  userDetails: UserDetailsState;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
interface ActivityLog {
  _id: string;
  activityType: string;
  name: string;
  description: string;
  values: any | null;
  customer?: string;
  application: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface IsManualAddress {
  show: boolean;
  customerId: string | null;
}

interface PersonalFinLoaders {
  id: string;
  addAssetLoader: boolean;
  removeAssetLoader: boolean;
  addLiabilityLoader: boolean;
  removeLiabilityLoader: boolean;
  addIncomeLoader: boolean;
  removeIncomeLoader: boolean;
  addExpenseLoader: boolean;
  removeExpenseLoader: boolean;
}

interface CreditScoreType {
  label: string;
  value: string;
  permissionType: string;
  productDataLevel: string;
}

interface IntercomChatData {
  user_id: string;
  name: string;
  email: string;
}

interface IntercomChatOptions {
  showIntercom: boolean;
  intercomData?: IntercomChatData | null;
  service?: string;
}

interface IRemoveAssetModalOptions {
  isOpen: boolean;
  assetType: string;
  assetId: string;
  isRemovingAsset: boolean;
}

interface IAssetLoaderOptions {
  assetId: string;
  isLoading: boolean;
}

interface ISendingInsuranceQuote {
  loading: boolean;
  to: (typeof INSURANCE_QUOTE_TO)[keyof typeof INSURANCE_QUOTE_TO];
}

interface IShowPreviewDocument {
  isOpen: boolean;
  customerId: string;
  templateType: string;
  disclosureInfo?: Record<string, any> | null;
}

interface UserDetailsState {
  demoLoanDetailsFields: Record<string, any>;
  application: ApplicationState;
  singleEntityElement: Record<string, any>;
  gettingQuote: boolean;
  confirmCustomerDataLoader: boolean;
  gettingClonedApplication: boolean;
  startApplicationBtnLoader: boolean;
  isNewApplication: boolean | undefined;
  removeAssetModalOptions: IRemoveAssetModalOptions;
  cloneAssetLoaderOptions: IAssetLoaderOptions;
  makeAssetPrimaryLoaderOptions: IAssetLoaderOptions;
  gettingCalculator: boolean;
  loadingSubmitApplication: boolean;
  submittingApplicationToEmail: boolean;
  verifiedDialogBoxIsOpen: boolean;
  customerPrivacyRequest: boolean;
  idVerificationRequest: boolean;
  bankStatementsRequest: boolean;
  bankStatementsLoading: boolean;
  linkSent: boolean;
  sendingIDLink: null | boolean;
  ApplicationBtn: boolean;
  gettingASICData: null | boolean;
  gettingASICPPSRData: null | boolean;
  gotASICData: null | boolean;
  gettingEntityDetails: boolean;
  gettingCreditProfile: boolean;
  gettingEquifaxCreditFile: boolean;
  gettingEquifaxCreditCompanyFile: boolean;
  isDeletingDocument: boolean;
  canCalculateQuote: boolean;
  gettingAddressDetails: boolean;
  isManualAddress: IsManualAddress;
  currentProductLenderId: string;
  directors: any[];
  shareHolders: any[];
  beneficiaries: any[];
  shares: any[];
  businessAddresses: any[];
  applicants: any[];
  entityId: string;
  entity: Record<string, any>;
  identityVerification: any[];
  identityDocument: any[];
  address: Record<string, any>;
  customer: Record<string, any>;
  document: Record<string, any>;
  creditProfile: Record<string, any>;
  url: string;
  open: boolean;
  uploadPrivacyLoading: string;
  uploadIDLoading: string;
  documentUploadLoding: string;
  entityDocumentUploadLoadingId: string;
  isUpdatedLoanDetails: null | boolean;
  gettingApplication: boolean;
  updatingApplicationStatus: boolean;
  downloadApplicationLoader: boolean;
  createProductQuoteLoader: boolean;
  updateProductQuoteLoader: boolean;
  removeProductQuoteLoader: boolean;
  productQuoteNoteModal: {
    open: boolean;
    mode: "save" | "update";
    productQuoteId: string | null;
    initialNotes: string;
  };
  setPrimaryProductQuoteLoaderOptions: {
    productQuoteId: string | null;
    isLoading: boolean;
  };
  sendProductQuotesToCustomersLoader: boolean;
  disclosureLoader: boolean;
  documentLinkSentLoader: boolean;
  documentPreview: null;
  showPreviewDocument: IShowPreviewDocument;
  activityLog: ActivityLog[];
  applicationServiceLog: any[];
  creditScoreTypes: CreditScoreType[];
  applicationServiceLogLoader: boolean;
  openManualQuote: boolean;
  openProductsQuotes: boolean;
  removeEmployerLoader: boolean;
  updatingApplicationFollowers: boolean;
  isConfirmingShareType: boolean;
  updatingCreditScoreModeCusId: string | null;
  personalFinLoaders: PersonalFinLoaders;
  removingEntityOptions: {
    isLoading: boolean;
    entityId: string;
  };
  requireFieldErrors: Record<string, any>;
  validationRequireFieldError: Record<string, any>;
  lenderRequiredFieldsError: Record<string, any>;
  calculatorFieldsErrors: Record<string, any>;
  gettingactivityLog: boolean;
  assetDocumentUploadLoading: boolean;
  isQuoteOpen?: boolean;
  isIntercomChatOptions?: IntercomChatOptions;
  /** In-flight insurance quote requests, keyed by asset id. */
  gettingInsuranceQuote: Record<string, boolean>;
  sendingInsuranceQuote: ISendingInsuranceQuote;
  /** Expanded section ids, keyed by asset id. */
  assetExpandedSections: Record<string, string[]>;
  /** In-flight CreditorWatch checks, keyed by entity id. */
  gettingCommercialCreditChecks: Record<string, Partial<Record<CommercialCreditCheck, boolean>>>;
  /** In-flight Equifax employment and income verifications, keyed by customer id. */
  requestingEmploymentIncomeVerification: Record<string, boolean>;
}

interface Email {
  status: string;
  _id: string;
  address: string;
  emailType: string;
  user: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface LenderSetting {
  _id: string;
  organisationId?: string;
  lender: string;
  user: string;
  lenderEmail?: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface Organisation {
  businessNames: any[];
  users: string[];
  organisations: any[];
  transactions: any[];
  services: any[];
  _id: string;
  workOSId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
  brandId: string;
}

interface User {
  isStripeCustomer: boolean;
  isBillable: boolean;
  lenderSettings: LenderSetting[];
  transactions: any[];
  services: any[];
  bankDetails: any[];
  status: string;
  service_invoice_paid_by: string;
  lenders: any[];
  _id: string;
  email: Email;
  terms: boolean;
  firstName: string;
  lastName: string;
  workOSId: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
  organisation: Organisation;
  userType: string;
}

interface PPSRSearchResponse {
  result_sequence_number: number;
  restriction_detail: any | null;
  registration_detail: AssetRegistrationDetail;
  change_history: ChangeDetails;
}

interface AssetRegistrationDetail {
  registration_number: string;
  change_number: number;
  earlier_registration_number: string | null;
  is_transitional: boolean;
  is_migrated: boolean;
  registration_start_time: string;
  registration_end_time: string;
  registration_change_time: string;
  secured_parties: SecuredParty[];
  grantors: Grantor[];
  giving_of_notice_identifier: string;
  address_for_service: AddressForService;
  registration_kind: string;
  is_security_interest_registration_kind: boolean;
  collateral_type: string;
  collateral_class_type: string;
  collateral_class_description: string;
  serial_number_details: SerialNumberDetails;
  collateral_description: string | null;
  is_pmsi: string;
  is_inventory: string;
  are_assets_subject_to_control: string | null;
  are_proceeds_claimed: string;
  proceeds_claimed_description: string;
  is_subordinate: boolean;
  migration_detail: any | null;
  registrar_alert: any | null;
}

interface Individual {
  family_name: string | null;
  given_names: string | null;
}
interface SecuredParty {
  secured_party_type: string;
  organisation: AssetOrganisation | null;
  individual: Individual | null;
}

interface AssetOrganisation {
  organisation_number_type: string;
  organisation_number: string;
  organisation_name: string;
  organisation_number_verification_status: string | null;
}

interface Grantor {
  grantor_type: string;
  organisation: AssetOrganisation | null;
  individual: Individual | null;
}

interface AddressForService {
  addressee: string;
  email_address: string;
  fax_number: string | null;
  mailing_address: MailingAddress;
  physical_address: string | null;
}

interface MailingAddress {
  iso_3166_country_code: string;
  country_name: string;
  line_1: string;
  line_2: string | null;
  line_3: string | null;
  locality: string;
  postcode: string;
  state: string;
}

interface SerialNumberDetails {
  serial_number_type: string;
  serial_number: string;
  additional_vehicle_details: AdditionalVehicleDetails;
}

interface AdditionalVehicleDetails {
  vehicle_registration_number: string | null;
  vehicle_descriptive_text: string | null;
}

interface ChangeDetails {
  change_details: ChangeDetail[];
}

interface ChangeDetail {
  change_number: number;
  change_type: string;
  registration_change_time: string;
}

interface BlueflagPPSR {
  search: PPSRSearchResponse[];
  nevdisData: Record<string, any>;
}

export const TypeOfSale = {
  AuthorisedSeller: "Authorised seller",
  AuthorisedElectricVehicleSeller: "Authorised electric vehicle seller",
  PrivateSale: "Private sale",
  MidTermRefinance: "Mid-term refinance",
  BalloonResidualFinance: "Balloon / Residual Refinance",
  SaleAndBuybackEquity: "Sale & buyback / Equity",
} as const;

/**
 * Glass's NVDB model payloads use ModelTypeCode A (passenger), B (motorcycle), C (caravan/trailer).
 * Same-named fields shared by all three response shapes.
 */
interface GlassesGuideNvdbModelCore {
  NVIC_Model: string;
  ManufacturerCode: string;
  FamilyCode: string;
  VariantCode: string;
  SeriesCode: string;
  SegmentCode: string;
  ClassCode: string;
  CountryCode: string;
  ReleaseDate: string;
  DiscontinueDate: string;
  ManufacturerName: string;
  ClassName: string;
  SegmentName: string;
  FamilyName: string;
  CountryName: string;
  BodyName: string;
  ModelName: string;
  NVIC_CUR: string;
  RRP: string;
  YearCreate: string;
  TradeLow: string;
  Trade: string;
  Retail: string;
}

/** Fields common to ModelTypeCode B and C only (not present on A in the same form). */
interface GlassesGuideNvdbModelBcExtension {
  DCCode: string;
  MarginCode: string;
  serv_Kms: string;
  Serv_Mths: string;
  VariantName: string;
}

/**
 * Semantic equivalents: same meaning, different API keys across types.
 * Use when normalizing rows for display or persistence.
 */
const GLASSES_NVDB_SEMANTIC_KEYS = {
  serviceIntervalKms: { A: "Service_KMS", B: "serv_Kms", C: "serv_Kms" },
  serviceIntervalMonths: { A: "Service_Months", B: "Serv_Mths", C: "Serv_Mths" },
  /** Curb / tare-style mass: A and B use KerbWeight; C uses Weight. */
  curbMassKg: { A: "KerbWeight", B: "KerbWeight", C: "Weight" },
} as const;

/** ModelTypeCode A — adds passenger-vehicle naming and A-specific service interval keys. */
interface GlassesGuideNvdbModelA extends GlassesGuideNvdbModelCore {
  ModelTypeCode: "A";
  Service_Months: string;
  Service_KMS: string;
  [key: string]: string | undefined;
}

/** ModelTypeCode B — motorcycle; extends core + B/C extension. */
interface GlassesGuideNvdbModelB extends GlassesGuideNvdbModelCore, GlassesGuideNvdbModelBcExtension {
  ModelTypeCode: "B";
  [key: string]: string | undefined;
}

/** ModelTypeCode C — caravan / camper trailer; extends core + B/C extension. */
interface GlassesGuideNvdbModelC extends GlassesGuideNvdbModelCore, GlassesGuideNvdbModelBcExtension {
  ModelTypeCode: "C";
  [key: string]: string | undefined;
}

type GlassesGuideNvdbModel = GlassesGuideNvdbModelA | GlassesGuideNvdbModelB | GlassesGuideNvdbModelC;

/**
 * AutoGrab's mapped vehicle. It carries Glass's-style aliases beside AutoGrab's own names
 * (`CCName` / `EngineCapacity`, `KW` / `PowerKW`) but never `ClassCode` / `ModelTypeCode`, which is
 * what tells the two payloads apart.
 */
interface AutograbSpecifications {
  /** AutoGrab's id for the vehicle. Its presence on the asset is what marks it AutoGrab-identified. */
  vehicleId?: string;
  assetLookupSource?: string;
  Description?: string;
  ModelName?: string;
  YearCreate?: string;
  ModelYear?: string;
  ManufacturerName?: string;
  FamilyName?: string;
  VariantName?: string;
  SeriesName?: string;
  BodyType?: string;
  BodyName?: string;
  Transmission?: string;
  TransmissionName?: string;
  FuelType?: string;
  FuelName?: string;
  EngineType?: string;
  EngineName?: string;
  DriveType?: string;
  DrivenWheelsName?: string;
  Doors?: string;
  NoOfDoors?: string;
  Seats?: string;
  NoOfSeats?: string;
  Gears?: string;
  Cylinders?: string;
  EngineCapacity?: string;
  CCName?: string;
  PowerKW?: string;
  KW?: string;
  TorqueNM?: string;
  Torque?: string;
  /** Valuation, in dollars. `Price` repeats whichever of retail / trade the search was priced on. */
  RRP?: string | number;
  Price?: string | number;
  RetailPrice?: string | number;
  RetailLower?: string | number;
  RetailUpper?: string | number;
  TradePrice?: string | number;
  TradeLower?: string | number;
  TradeUpper?: string | number;
  /** Odometer the valuation was struck at, spelled as the server sends it. */
  KMS?: string | number;
  /** AutoGrab's 0-1 confidence that this is the vehicle that was searched for. */
  Score?: string | number;
  [key: string]: string | number | undefined;
}

// Full Specifications for A model code
interface Specifications {
  NVIC_CUR: string;
  YearCreate: string;
  ManufacturerName: string;
  FamilyName: string;
  VariantName: string;
  SeriesName: string;
  BodyName: string;
  CCName: string;
  TransmissionName: string;
  NVIC_Model: string;
  ModelName: string;
  ReleaseDate: string;
  DiscontinueDate: string;
  TradeLow: string;
  Trade: string;
  Retail: string;
  PrivateSale: string;
  DealerRetail: string;
  DealerTrade: string;
  RRP: string;
  SegmentName: string;
  CountryName: string;
  AverageKM: string;
  ClassName: string;
  ClassCode: string;
  MarketingModelCode: string;
  MTH: string;
}

interface GlassesListOptions {
  name: string;
  value: string;
}

interface Make {
  name: string | null;
  code: string | null;
}

interface Model {
  Name: string | null;
  Code: string | null;
}

interface Variant {
  Name: string | null;
}

/** A row in the picker. Identity and label only — the specification is resolved on selection. */
interface NVIC {
  ModelName: string | null;
  NVIC_CUR: string | null;
  NVIC_MODEL: string | null;
}

/**
 * `monthlyPricing.provider` as the insurer sends it: the values the server's `INSURANCE_MONTHLY_PRICING_PROVIDER`
 * stores.
 */
type InsuranceMonthlyPricingProvider = "CloudFloat";

/** `status` as the insurer sends it: the values the server's `INSURANCE_QUOTE_STATUS` stores. */
type InsuranceQuoteStatus = "quoteComplete";

/**
 * Paying the premium by instalments through a premium funder, as offered with the quote. Amounts are dollars to 2
 * decimal places.
 */
interface InsuranceMonthlyPricing {
  available: boolean;
  provider?: InsuranceMonthlyPricingProvider;
  annualPremium?: number;
  upfrontAmount?: number;
  recurringInstalmentAmount?: number;
  numberOfInstalments?: number;
  numberOfRecurringInstalments?: number;
  feePerInstalment?: number;
  totalPayable?: number;
  /** CloudFloat's id for its quote, e.g. `quote_0a1b2c3d4e5f`. */
  cloudfloatQuoteId?: string;
}

interface InsuranceQuote {
  status?: InsuranceQuoteStatus;
  /** Dollars to 2 decimal places. */
  estimatedPremium: number;
  quoteId: string;
  quoteGuid: string;
  /** Older quotes hold the insurer's text instead, e.g. `2026-10-19`, which `formatToReadableDate` reads the same. */
  inceptionDate?: CalendarDate;
  expiryDate?: CalendarDate;
  periodOfCover: string;
  allowSendToCustomer: boolean;
  allowSendToBroker: boolean;
  customerQuoteLink: string;
  brokerQuoteLink: string;
  monthlyPricing?: InsuranceMonthlyPricing;
}

interface Asset {
  assetType?: string;
  condition?: string;
  blueflagPPSR: BlueflagPPSR;
  documents: Document[];
  ageOfAsset?: string;
  ageOfAssetAtEnd: string;
  assetValue: number;
  netAssetValue: number;
  additionalFees: number;
  additionalTaxes: number;
  isManualAssets: boolean;
  _id: string;
  application: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
  supplierABN?: string;
  supplierAddress?: string;
  supplierEmail?: string;
  supplierName?: string;
  supplierContactName?: string;
  supplierPhone?: string;
  make?: string;
  model?: string;
  glassesMake?: string;
  glassesModel?: string;
  glassesVariant?: string;
  glassesNVIC?: string;
  addOns?: AddOns[];
  odometer?: string;
  serialNumber?: string;
  specifications?: GlassesGuideNvdbModel | AutograbSpecifications;
  typeOfSale?: (typeof TypeOfSale)[keyof typeof TypeOfSale];
  registrationNumber?: string;
  vin?: string;
  registrationState?: string;
  purpose?: string;
  description?: string;
  vehicleData?:
    | null
    | boolean
    | ({
        // Only a Glasses-sourced lookup answers an NVIC; AutoGrab answers the specification itself.
        nvic?: string;
      } & Record<string, unknown>);
  isNVICAbsence?: boolean;
  NVICData?: any;
  /** Which provider produced the glasses* fields. Undefined on Glasses-sourced assets. */
  assetLookupSource?: string;
  /** The identified vehicle's id in that provider's catalogue — an AutoGrab vehicle id today. */
  vehicleId?: string;
  insuranceQuote?: InsuranceQuote;
  // Custom fields
  nvicLoaderData: boolean;
  /** The vehicle the in-flight specification call is for, so a slower earlier pick is dropped. */
  requestedVehicleId?: string;
  makeLoader: boolean;
  modelLoader: boolean;
  variantLoader: boolean;
  NVICLoader: boolean;
  searchVehicleLoader: boolean;
  searchPPSRLoader: boolean;
  registrationNumberOrVin?: string;
  listOfMakes?: string[];
  listOfModels?: string[];
  listOfVariants?: string[];
  NVICList?: string[];
  /** Free-text vehicle search, kept apart from the cascade's `NVICList`. */
  plainTextSearchLoader?: boolean;
  plainTextSearchList?: NVIC[];
  /** Term of the in-flight request, used to discard responses that arrive out of order. */
  plainTextSearchTerm?: string;
  /** The last search failed, as opposed to answering with nothing. */
  plainTextSearchError?: boolean;
}

enum FeeFrequency {
  Monthly = "monthly",
  Upfront = "upfront",
  Financed = "financed",
}

interface Criteria {
  label: string;
  object: string;
  attribute: string;
  condition: string;
  value: { min: number; max: number } | string;
}

interface Fee {
  key?: string;
  name: string;
  value?: number;
  calc?: "percent" | "dollar";
  capitalised?: boolean; // ## TODO: This type is being deprecated.
  frequency: FeeFrequency;
  criteria?: Criteria[];
  referrer?: string;
}

enum InArrears {
  Yes = "Yes",
  No = "No",
}

interface LoanDetails {
  repayments: number;
  loanAmount: number;
  requestedLoanAmount: number;
  purpose?: string;
  term?: number;
  brokerage: number;
  brokerageAmount: number;
  brokerageAdjustment?: number;
  balloon: number;
  balloonAmount: number;
  deposit: number;
  tradeIn: number;
  originationFee: number;
  payout: number;
  fees: Fee[];
  rate: number;
  rateAdjustment: number;
  cash?: number;
  lender: string;
  inArrears?: InArrears;
  referrer?: string;
  _id: string;
  application: string;
  createdAt: string;
  updatedAt: string;
  productType?: string;
  __v: number;
}

interface PhoneNumber {
  _id: string;
  number: string;
  status: string;
  customer: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface CustomerProfile {
  numberOfDependants: number;
  _id: string;
  customer: string;
  __v: number;
  createdAt: string;
  maritalStatus?: string;
  updatedAt: string;
  residencyStatus?: string;
  visaType?: string;
  visaExpiryDate?: string;
  livingArrangements?: string;
  /** Share of the household expenses carried by this customer, as a fraction of 1. */
  shareOfExpenses?: number;
  /** Share of the HEM benchmark carried by this customer, as a fraction of 1. */
  shareOfHem?: number;
  serviceabilitySurplus?: number;
  /** Free text comment a broker records against this customer's serviceability. */
  serviceabilityComment?: string;
}

interface CustomerDisclaimer {
  _id: string;
  customer: string;
  application: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
  anyForeseeableChangesImpactingLoanRepayment: string;
  balloonPaymentAtTheEndOfTheLoan: string;
  howApplicantIntendToDoLoanRepaymentAfterChange: string;
  whyIsThisLoanNotUnsuitableForTheApplicantDetail: string;
  increaseInLoanAmountForFinancingCostOfInsurance: string;
  whatAreYouLookingForInYourLoan: string;
  whatIsThePurposeOfThisApplication: string;
  whatToDoWithBalloonPaymentAtTheEndOfTheLoanTerm: string;
  whyDoesThisLoanTermSuitYourRequirements: string;
}

interface Customer {
  isCustomerDetailsSaved: boolean;
  isDetailsLinkSend: boolean;
  isDocumentRequestLinkSend?: boolean;
  documents: Document[];
  addresses: any[];
  emails: any[];
  phoneNumbers: PhoneNumber[];
  applications: string[] | ApplicationState[];
  entities: any[];
  employers: any[];
  income: Array<Record<string, any>>;
  expenses: Array<Record<string, any>>;
  customerAssets: Array<Record<string, any>>;
  liabilities: Array<Record<string, any>>;
  users: any[];
  bankDetails: any[];
  _id: string;
  customerDisclaimer: CustomerDisclaimer;
  createdAt: string;
  updatedAt: string;
  __v: number;
  customerTitle: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  IDNumber: string;
  IDCardNumber: string;
  IDExpiryDate: string;
  customerProfile: CustomerProfile;
  creditProfile: Record<string, any>;
  /** The latest Equifax verification summary; an id where a payload did not populate it. */
  employmentIncomeVerification?: EmploymentIncomeVerificationSummary | string | null;
  role?: string;
  appointmentDate?: string;
  shares?: number;
}

/**
 * Equifax's code tables (Verification Exchange API guide, section 5), spelt as Equifax spells them: the values the
 * server's `utils/equifaxVerificationExchangeCodes.ts` stores.
 */
type EquifaxEmploymentStatus =
  | "Active"
  | "On International Assignment"
  | "Workers Compensation"
  | "Personal Leave"
  | "No Longer Employed"
  | "Inactive"
  | "On Leave"
  | "Lay Off"
  | "Retired"
  | "Separated"
  | "Intern"
  | "Terminated with Notice"
  | "On Approved Unpaid Leave"
  | "Temporarily Inactive"
  | "On Long Service Leave"
  | "On Community Service Leave"
  | "On Parental Leave"
  | "On Secondment";

type EquifaxEmploymentStatusType =
  | "Full-time Permanent"
  | "Part-time Permanent"
  | "Casual"
  | "Full-time Fixed Term"
  | "Part-time Fixed Term"
  | "Full-time"
  | "Part-time"
  | "Labour Hire"
  | "Pension and Annuity"
  | "Voluntary Agreement"
  | "Death Beneficiary"
  | "Non-Employee";

type EquifaxPayRateDescription =
  | "Annual"
  | "Semi-annual"
  | "Monthly"
  | "Bi-weekly"
  | "Weekly"
  | "Daily"
  | "Hourly"
  | "13 Times/year"
  | "Commission Only"
  | "Owner Base Pay"
  | "Hourly w/o Commission"
  | "Hourly + Commission"
  | "Quarterly"
  | "Monthly Pension"
  | "Hourly or Commission"
  | "Per Trip"
  | "Per Task"
  | "Full-time Permanent"
  | "Part-time Permanent";

type EquifaxPayFrequency = "Semi-annually" | "Monthly" | "Fortnightly" | "Weekly";

type EquifaxYearToDateType = "Calendar Year" | "Australian Financial Year";

/** The product ordered, named by the months of history it covers: `M3`, `M12` or `M36`. */
type EquifaxVerificationProductId = `M${EmploymentIncomeVerificationMonths}`;

/**
 * A date with no time of day, never Equifax's `dd/mm/yyyy` text: the server stores a `Date` at midnight UTC, which
 * JSON carries as ISO 8601, year first, e.g. `2019-05-31T00:00:00.000Z` for 31 May 2019. `formatToReadableDate` shows
 * it day first as the app does (`31-05-2019`), whatever the time zone.
 */
type CalendarDate = string;

/** The verification summary the server populates onto a customer. It never carries the raw payroll response. */
interface VerifiedEmployment {
  employmentStatus?: EquifaxEmploymentStatus;
  employmentType?: EquifaxEmploymentStatusType;
  /** Free text from the employer's payroll, e.g. `Project Manager`. */
  jobTitle?: string;
  /** Free text from the employer's payroll, e.g. `Finance`. */
  division?: string;
  originalHireDate?: CalendarDate;
  mostRecentHireDate?: CalendarDate;
  lastDayWorked?: CalendarDate;
  totalTimeWithEmployerMonths?: number;
}

/** Amounts are whole Australian cents, e.g. `500050` for $5,000.50, and can be negative. */
interface VerifiedPayPeriod {
  startDate?: CalendarDate;
  endDate?: CalendarDate;
  payFrequency?: EquifaxPayFrequency;
  payRateCents?: number;
  payRateDescription?: EquifaxPayRateDescription;
  hoursWorked?: number;
  grossEarningsCents?: number;
  netEarningsCents?: number;
  basePayCents?: number;
}

/**
 * A payroll year spans one calendar year (`startYear` and `endYear` the same) or two in a row. Amounts are whole
 * Australian cents.
 */
interface VerifiedGrossIncome {
  startYear?: number;
  endYear?: number;
  yearType?: EquifaxYearToDateType;
  basePayCents?: number;
  totalCents?: number;
}

interface VerifiedEmployer {
  /** The employer's name as Equifax holds it, e.g. `Acme Pty Ltd`. */
  employerName?: string;
  /** The employer's ABN: 11 digits with no spaces. */
  employerAbn?: string;
  dataCurrentAsOf?: CalendarDate;
  employments?: VerifiedEmployment[];
  latestPayPeriod?: VerifiedPayPeriod;
  latestGrossIncome?: VerifiedGrossIncome;
}

interface EmploymentIncomeVerificationSummary {
  _id: string;
  status: EmploymentIncomeVerificationStatus;
  productId: EquifaxVerificationProductId;
  /** Equifax's reference for the order: a UUID, or a long number kept as a string. */
  transactionNumber?: string;
  /** Equifax's free-text note on the result, shown on a no-match, e.g. `No record found`. */
  message?: string;
  verifiedEmployers?: VerifiedEmployer[];
  reportPdfStatus?: EmploymentIncomeVerificationReportPdfStatus;
  document?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface Address {
  _id: string;
  unitNumber: string | null;
  addressType: string;
  fullAddress: string;
  city: string;
  postCode: string;
  streetNumber: string;
  streetType: string;
  street: string;
  state: string;
  country: string;
  status: string;
  entity: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface Entity {
  _id: string;
  businessNames: string[];
  entityName: string;
  status: string;
  abn: string;
  acn: string;
  state: string;
  entityType: string;
  gst: string;
  abnStatusEffectiveFrom: string;
  timeInBusiness: string;
  directors: any[];
  shareHolders: any[];
  beneficiaries: any[];
  shares: any[];
  turnover: number;
  industryType: string;
  professionalServices?: string;
  businessProfile: any[];
  bankDetails: any[];
  documents: any[];
  customers: any[];
  applications: string[];
  addresses: Address[];
  emails: any[];
  phoneNumbers: any[];
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface SubmissionStep {
  type: "acknowledgement" | "documents" | "confirmation" | "mfa";
  label: string;
  body?: string | null;
  stepper: string;
  isCompleted?: boolean;
}

interface SubmissionModal {
  steps: SubmissionStep[];
  isOpen?: boolean;
  submissionType?: "email" | "upload" | "browser";
  lender: string | undefined;
  lodgeAI?: LodgeAI | undefined;
  productDetails?: { productName: string; productType: string };
  acknowledgement?: "pending" | "fulfilled" | "rejected" | undefined;
  submission: {
    error?: LenderErrorV1["error"] | LenderError["error"];
    status: "pending" | "fulfilled" | "rejected" | undefined;
    message?: string;
    applicationNo?: string;
  };
  selectedDocuments?: string[];
}

interface Document {
  _id: string;
  name: string;
  documentType: string;
  details: null | any;
  customer?: string;
  asset?: any;
  entity?: any;
  application: string;
  ext: string;
  fileName: string;
  url: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface CustomerAcknowledgement {
  is_acknowledgement_accept: boolean;
  acknowledgement_text?: string;
}

interface Quote {
  repayments?: number;
  repaymentFrequency?: (typeof RepaymentFrequency)[keyof typeof RepaymentFrequency];
  loanAmount?: number;
  requestedLoanAmount?: number;
  term?: number;
  brokerage?: number;
  brokerageAmount?: number;
  brokerageAdjustment?: number;
  brokerageFinanced?: string;
  balloon?: number;
  balloonAmount?: number;
  deposit?: number;
  tradeIn?: number;
  originationFee?: number;
  payout?: number;
  fees?: any;
  rate?: number;
  rateAdjustment?: number;
  commission?: number;
  lender?: string;
  productType?: string;
  inArrears?: string;
  application: string;
  notes?: string;
}

interface ProductsQuote {
  _id: string;
  lender: string;
  type: ApplicationType;
  lenderId: string;
  applicationId: string;
  quote: Quote;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface ApplicationFollower {
  user: string;
  permission: string;
}

interface ApplicationState {
  canEditApplication: boolean;
  asset: Asset;
  loanDetails: LoanDetails;
  additionalAssets?: Asset[]; // TODO: Optional due to Asset Section Uplift Feature Flag make it required when it live
  customer_acknowledgement: CustomerAcknowledgement;
  customers: Customer[];
  entities: Entity[];
  transactions: any[];
  activities: string[];
  comments: any[];
  _id: string;
  humanId: string;
  status: string;
  user: User;
  applicationType: ApplicationType;
  purpose?: string;
  createdAt: string;
  updatedAt: string;
  submissionModal: SubmissionModal;
  productsQuotes: ProductsQuote[];
  documents?: {
    status: "pending" | "fulfilled" | "rejected" | undefined;
    asset?: Document[];
    customer?: Document[];
    entity?: Document[];
  };
  introducerDeclarationAgree?: boolean;
  followers?: ApplicationFollower[];
  __v: number;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

enum ApplicationType {
  Consumer = "consumer",
  Commercial = "commercial",
  Personal = "personal",
  Business = "business",
}

export type {
  ApplicationFormSlice,
  UserDetailsState,
  ActivityLog,
  IsManualAddress,
  PersonalFinLoaders,
  CreditScoreType,
  ProductsQuote,
  ApplicationState,
  LoanDetails,
  Fee,
  AddOns,
  SecuredParty,
  PPSRSearchResponse,
  BlueflagPPSR,
  Specifications,
  GlassesListOptions,
  GlassesGuideNvdbModelCore,
  GlassesGuideNvdbModelBcExtension,
  GlassesGuideNvdbModelA,
  GlassesGuideNvdbModelB,
  GlassesGuideNvdbModelC,
  GlassesGuideNvdbModel,
  AutograbSpecifications,
  Make,
  Model,
  Variant,
  NVIC,
  InsuranceMonthlyPricing,
  Asset,
  SubmissionModal,
  SubmissionStep,
  Customer,
  Entity,
  CustomerDisclaimer,
  Document,
  EmploymentIncomeVerificationSummary,
  VerifiedEmployer,
  VerifiedEmployment,
};

export { ApplicationType, FeeFrequency, InArrears, RepaymentFrequency, GLASSES_NVDB_SEMANTIC_KEYS };
